/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useMemo, useState, useCallback } from 'react'
import { t } from '@sportninja/common/i18n'
import { getTeamOfficialTypesBySportId } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { Table, Typography, Avatar } from 'antd'
import { getTableStyles } from 'src/styles/tableStyles'
import useWindowSize from 'src/hooks/useWindowSize'
import Icon from 'src/components/Icon'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { SendInvitationTrigger } from 'src/components/SendInvitationModal'
import OptionButtonDelete from 'src/components/List/OptionButtons/Delete'
import MoreOptions from 'src/components/List/OptionButtons/More'
import TeamOfficialModal from 'src/components/TeamOfficialModal'
import { formatPhoneNumber } from 'react-phone-number-input'
import { useRosterContext } from '../context/RosterContext'
import { useUIContext } from '../context/UIContext'
import { useScrollToRow } from '../hooks/useScrollToRow'
import SectionTitle from './SectionTitle'

interface TeamOfficialSectionProps {
  teamId: string
  permission: Record<string, any>
  teamOfficialTypes: any[]
  sportId?: string
}

const TeamOfficialSection = ({
  teamId,
  permission,
  teamOfficialTypes,
  sportId,
}: TeamOfficialSectionProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 1024, [width])
  const { teamOfficials, selectedRosterId, refresh } = useRosterContext()
  const {
    highlightedRows,
    addHighlightedRow,
    selectedOfficial,
    setSelectedOfficial,
    isOfficialModalOpen,
    setOfficialModalOpen,
  } = useUIContext()
  const scrollToRow = useScrollToRow()

  const [sort, setSort] = useState('name_last')
  const [direction, setDirection] = useState('asc')

  const handleSort = (field: string, order: string) => {
    setSort(field)
    setDirection(order)
  }

  const handleEditClick = useCallback(
    (item: any) => {
      setSelectedOfficial(item)
      setOfficialModalOpen(true)
    },
    [setSelectedOfficial, setOfficialModalOpen]
  )

  const handleModalClose = useCallback(() => {
    setOfficialModalOpen(false)
    setSelectedOfficial(null)
  }, [setOfficialModalOpen, setSelectedOfficial])

  const handleModalSuccess = useCallback(async () => {
    await refresh()
    if (selectedOfficial?.id) {
      addHighlightedRow(selectedOfficial.id, 'success')
      scrollToRow(selectedOfficial.id)
    }
  }, [refresh, selectedOfficial, addHighlightedRow, scrollToRow])

  const getOptions = useCallback(
    (item: any) => {
      const slug = `/teams/${teamId}/rosters/${selectedRosterId}/team-officials/${item.id}`

      const opts = [
        <MoreOptions
          key='more'
          options={[
            // @ts-ignore
            {
              key: 'edit',
              title: capitalizeWords('Edit Profile'),
              iconName: 'pencil',
              onClick: () => handleEditClick(item),
            },
            // Renders as Resend Invitation or Send Invitation depending on the
            // row's invite state. Hidden on exactly the rows
            // SendInvitationTrigger renders nothing for (invited AND accepted).
            // @ts-ignore
            !(item.is_invited && item.is_accepted) && {
              key: 'invite',
              Component: SendInvitationTrigger,
              componentProps: {
                asMenuItem: true,
                invite: {
                  is_invited: item.is_invited,
                  is_accepted: item.is_accepted,
                  invitation_id: item.invitation_id,
                },
                user: {
                  id: item.id,
                  name_first: item.name_first,
                  name_last: item.name_last,
                  team_official_type_id: item.team_official_type,
                  email: item?.email,
                },
                slug,
                onComplete: refresh,
              },
            },
            // @ts-ignore
            {
              key: 'remove',
              Component: OptionButtonDelete,
              componentProps: {
                buttonText: 'remove',
                slug,
                iconName: 'user-minus',
                title: `Remove ${item.name_full}`,
                type: t('common:teamOfficial', {}),
                itemId: item.id,
                onComplete: refresh,
              },
              title: 'remove',
              iconName: 'user-minus',
            },
          ]}
        />,
      ]

      return opts.filter((o) => o !== undefined)
    },
    [teamId, selectedRosterId, refresh, handleEditClick]
  )

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: t('common:name', {}),
        key: 'name',
        sorter: true,
        width: isMobile ? 200 : 300,
        ellipsis: true,
        showSorterTooltip: false,
        sortOrder:
          sort === 'name_first' || sort === 'name_last'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_: any, item: any) => (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              width: 100%;
              max-width: 268px;
              overflow: hidden;
              color: ${colors.NEUTRAL_0};
            `}
          >
            <Avatar
              src={
                getImageThumbnailId(item) ||
                '/images/generic_placeholders/placeholder_player.png'
              }
              icon={<Icon name='user' fontSize={14} />}
              shape='circle'
              css={css`
                border: 1px solid #dbdbdb;
                background-color: white;
                padding: 2px;
                flex-shrink: 0;
                width: 32px;
                height: 32px;
              `}
            />
            <div
              css={css`
                flex: 1;
                min-width: 0;
                overflow: hidden;
                display: flex;
                flex-direction: column;
              `}
            >
              <Typography.Text
                strong
                ellipsis
                css={css`
                  display: block;
                  width: 100%;
                  color: inherit;
                `}
              >
                {item?.name_first} {item?.name_last}
              </Typography.Text>
            </div>
          </div>
        ),
      },
      {
        title: t('common:role', {}),
        key: 'role',
        sorter: true,
        showSorterTooltip: false,
        width: 150,
        sortOrder:
          sort === 'official_type'
            ? direction === 'asc'
              ? 'ascend'
              : 'descend'
            : null,
        render: (_: any, item: any) =>
          teamOfficialTypes.find((t: any) => t.id === item.team_official_type)
            ?.name_full || '-',
      },
      {
        title: t('common:email', {}),
        key: 'email',
        dataIndex: 'email',
        render: (email: string) => email || '',
      },
      {
        title: t('common:phone', {}),
        key: 'phone_number',
        dataIndex: 'phone_number',
        render: (phoneNumber: string) => formatPhoneNumber(phoneNumber) || '',
      },
      {
        title: 'Invite Status',
        key: 'invite_status',
        width: 180,
        render: (_: any, item: any) => {
          if (item?.is_accepted) {
            return 'Accepted'
          }
          if (item?.is_invited) {
            return 'Invited'
          }
          return ''
        },
      },
    ]

    if (permission?.update || permission?.admin) {
      cols.push({
        title: '',
        key: 'options',
        align: 'right',
        width: 150,
        fixed: 'right',
        render: (_: any, item: any) => (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              justify-content: flex-end;
            `}
          >
            {getOptions(item)}
          </div>
        ),
      })
    }

    return cols
  }, [isMobile, sort, direction, teamOfficialTypes, permission, getOptions])

  const sorter = useCallback(
    (a: any, b: any) => {
      if (sort === 'name_last' || sort === 'name_first') {
        const nameA = `${a.name_last} ${a.name_first}`.toLowerCase()
        const nameB = `${b.name_last} ${b.name_first}`.toLowerCase()
        if (nameA < nameB) {
          return direction === 'asc' ? -1 : 1
        }
        if (nameA > nameB) {
          return direction === 'asc' ? 1 : -1
        }
        return 0
      }
      if (sort === 'official_type') {
        if (a.team_official_type < b.team_official_type) {
          return direction === 'asc' ? -1 : 1
        }
        if (a.team_official_type > b.team_official_type) {
          return direction === 'asc' ? 1 : -1
        }
        return 0
      }
      return 0
    },
    [sort, direction]
  )

  const sortedItems = useMemo(
    () => [...teamOfficials].sort(sorter),
    [teamOfficials, sorter]
  )

  if (sortedItems.length === 0) {
    return (
      <div>
        <SectionTitle>Team Officials</SectionTitle>
        <p
          css={css`
            color: ${colors.NEUTRAL_50};
            font-size: 14px;
          `}
        >
          No team officials have been added to this roster yet.
        </p>
      </div>
    )
  }

  const getRowClassName = (record: any) => {
    return highlightedRows.has(record.id) ? 'highlighted-row' : ''
  }

  return (
    <div
      css={css`
        width: 100%;

        @keyframes highlightFade {
          0% {
            box-shadow: inset 0 0 0 9999px ${colors.SUCCESS_300}4D;
          }
          60% {
            box-shadow: inset 0 0 0 9999px ${colors.SUCCESS_300}4D;
          }
          100% {
            box-shadow: inset 0 0 0 9999px transparent;
          }
        }
        .highlighted-row > td {
          animation: highlightFade 5s ease-out;
        }
      `}
    >
      <SectionTitle>Team Officials</SectionTitle>
      <Table
        columns={columns}
        dataSource={sortedItems}
        rowKey='id'
        pagination={false}
        size='small'
        scroll={{ x: 'max-content' }}
        css={getTableStyles(isMobile)}
        rowClassName={getRowClassName}
        onChange={(_pagination: any, _filters: any, tableSorter: any) => {
          if (tableSorter.order) {
            const sortKey = tableSorter.columnKey || tableSorter.field
            const field =
              sortKey === 'name'
                ? 'name_last'
                : sortKey === 'role'
                ? 'official_type'
                : sortKey
            handleSort(field, tableSorter.order === 'ascend' ? 'asc' : 'desc')
          } else {
            handleSort('name_last', 'asc')
          }
        }}
      />
      <TeamOfficialModal
        isOpen={isOfficialModalOpen}
        onClose={handleModalClose}
        initialData={selectedOfficial}
        teamId={teamId}
        rosterId={selectedRosterId}
        onSuccess={handleModalSuccess}
        sportId={sportId}
      />
    </div>
  )
}

const mapStateToProps = (state: any, { sportId }: any) => {
  return {
    teamOfficialTypes: getTeamOfficialTypesBySportId(state, sportId),
  }
}

export default connect(mapStateToProps)(TeamOfficialSection)
